import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  OutlinedInput,
  CircularProgress,
} from "@mui/material";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SearchIcon from "@mui/icons-material/Search";
import { Clear } from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import InputMask from "react-input-mask";
import SearchModal from "../../components/SearchModal";
import socket from "../../socket";
import { useSelector } from "react-redux";

// Define interface for form data
interface FormData {
  mrn?: number;
  patientName: string;
  guardiansName: string;
  gender: string;
  dob: string;
  phonNumber: string;
  cnic: string;
  healthId: string;
  city: string;
  reference: string;
}

// Define interface for modal data
interface ModalData {
  patientName: string;
  guardiansName: string;
  mrn: number;
}

const validationSchema = Yup.object().shape({
  mrn: Yup.number(),
  patientName: Yup.string().required("Patient Name is required"),
  guardiansName: Yup.string()
    .matches(/^[a-zA-Z\s]+$/, "Guardian's Name should contain only alphabets")
    .required("Guardian Name is required"),
  gender: Yup.string().required("Gender is required"),
  dob: Yup.string()
    .required("Date is required")
    .matches(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
  phonNumber: Yup.string()
    .transform((value) => (value ? value.replace(/-/g, "") : value))
    .matches(/^03\d{9}$/, "Phone number must start with 03")
    .required("Phone Number is required"),
  cnic: Yup.string()
    .transform((value) => (value ? value.replace(/[-\s]/g, "") : value))
    .test("is-length", "CNIC must be exactly 13 characters long", (value) =>
      value ? value.length === 13 : true
    ),
  healthId: Yup.string(),
  city: Yup.string().required("City is required"),
  reference: Yup.string(),
});

const RegForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedmrn } = location.state || {};

  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalData, setModalData] = useState<ModalData[]>([]);
  const [searchErrors, setSearchErrors] = useState<any>();
  const [formChanged, setFormChanged] = useState(false);
  const [searchType, setSearchType] = useState("MRN");
  const [searchValue, setSearchValue] = useState("");
  const [selectedMRN, setSelectedMRN] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [storeData, setStoreData] = useState<FormData | null>(null);
  const [age, setAge] = useState("");
  const [inputError, setInputError] = useState("");
  const [isUpdateAndNext, setIsUpdateAndNext] = useState(false);
  const profile = useSelector((state: any) => state?.profileSlice?.profile);

  
  // Helper function to navigate with doctor
  const navigateWithDoctor = async (patientId: any, lastDoctorId: string) => {
    try {
      // Fetch doctor details
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/getAllDoctors`,
        {
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      
      const doctors = response.data?.data;
      const savedDoctor = doctors?.find((doc: any) => doc._id === lastDoctorId);
      
      if (savedDoctor) {
        // Include doctor in patientId and navigate
        const patientIdWithDoctor = { ...patientId, doctor: savedDoctor };
        navigate("/patient_Appointment_slot", { state: { patientId: patientIdWithDoctor } });
      } else {
        // Doctor not found, go to doctor card page
        navigate("/doc_card", { state: { patientId } });
      }
    } catch (error) {
      console.error("Error fetching doctor:", error);
      // On error, go to doctor card page
      navigate("/doc_card", { state: { patientId } });
    }
  };
  const formik = useFormik<FormData>({
    initialValues: {
      mrn: 0,
      patientName: "",
      guardiansName: "",
      gender: "",
      dob: dayjs().format("YYYY-MM-DD"),
      phonNumber: "",
      cnic: "",
      healthId: "",
      city: "",
      reference: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (formChanged && selectedId) {
        axios
          .put(
            `${process.env.REACT_APP_BASE_URL}/patient-registration/updatePatient/${selectedId}`,
            values,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          )
          .then((response) => {
            if (response?.data?.isSuccess) {
              toast.success("Patient updated successfully!");
              setStoreData(response?.data?.data);
              const patientId = response?.data?.data;

              // Socket logic
              let socketData: {
                projectId: string;
                module: string;
                operation: string;
              } = {
                projectId: localStorage.getItem("projectOP")
                  ? localStorage.getItem("projectOP")!.toString()
                  : "",
                module: "patientRegistrations",
                operation: "update",
              };

              // Ensure socket connection
              socket.on("connect", () => {});

              socket.on("disconnect", () => {});

              // Emit the event to the server
              socket.emit("himsEvents", socketData, (val: any) => {});

              // Optionally remove event listeners to avoid potential memory leaks
              socket.off("connect");
              socket.off("disconnect");

              // Navigate based on which button was clicked
              if (isUpdateAndNext) {
                navigate("/doc_card", { state: { patientId } });
              } else {
                navigate("/appointment-slot");
              }
            } else {
              toast.error(response?.data?.message);
            }
          })
          .catch((error) => {
            console.error("Error updating patient:", error);
            toast.error("Server Error!");
          });
      } else {
        const data = { ...values };
        if (data.mrn) {
          delete data.mrn; // mrn is optional, so safe to delete
        }
        axios
          .post(
            `${process.env.REACT_APP_BASE_URL}/patient-registration/registerNewPatient`,
            data,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          )
          .then((response) => {
            if (response?.data?.isSuccess) {
              toast.success("Patient registered successfully!");
              setStoreData(response?.data?.data);
              const patientId = response?.data?.data;

              // Check if doctor is already saved in localStorage
              const lastDoctorId = localStorage.getItem('lastSelectedDoctorSlot');
              if (lastDoctorId) {
                // Fetch doctor and navigate with doctor object
                navigateWithDoctor(patientId, lastDoctorId);
              } else {
                // No saved doctor, go to doctor card page
                navigate("/doc_card", { state: { patientId } });
              }
            } else {
              toast.error(response?.data?.message);
            }
          })
          .catch((error) => {
            console.error("Error registering patient:", error);
            toast.error("Server Error!");
          });
      }
    },
  });

  const validateDate = (dateString: string) => {
    if (!dateString) return true;
    const parts = dateString.split(".");
    if (parts.length !== 3) return false;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (year < 1900 || year > new Date().getFullYear()) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    const date = dayjs(`${year}-${month}-${day}`);
    if (!date.isValid()) return false;
    return (
      date.isBefore(dayjs()) &&
      date.format("DD") === parts[0].padStart(2, "0") &&
      date.format("MM") === parts[1].padStart(2, "0") &&
      date.format("YYYY") === parts[2]
    );
  };

  useEffect(() => {
    setSelectedMRN(selectedmrn);
  }, [selectedmrn]);

  useEffect(() => {
    setFormChanged(true);
  }, [formik.values]);

  useEffect(() => {
    if (storeData) {
      setAge(DateOfBirthToAge(storeData?.dob));
      formik.setValues(storeData);
    }
  }, [storeData]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchErrors(null);
    const { name, value } = event.target;
    const newValue = value.replace(/-/g, "");
    if (name && newValue) {
      if (name === "cnic") {
        const cnicParts = newValue.match(/^(\d{5})(\d{7})(\d{1})$/);
        formik.setFieldValue(
          name,
          cnicParts ? cnicParts.slice(1).join("") : newValue
        );
      } else if (name === "phonNumber") {
        formik.setFieldValue(
          name,
          newValue.match(/^03\d{9}$/) ? newValue : newValue
        );
      } else {
        formik.setFieldValue(name, newValue);
      }
      formik.handleChange(event);
    } else {
      formik.setFieldValue(name, "");
      formik.handleChange(event);
    }
  };

  const [openModal, setOpenModal] = useState(false);
  const handleCloseModal = () => setOpenModal(false);
  const handleOpenModal = () => setOpenModal(true);

  const handleSearchTypeChange = (event: any) => {
    setSearchType(event.target.value);
    setSearchValue("");
  };

  const handleSearch = () => {
    setLoading(true);
    let endpoint = "";
    switch (searchType) {
      case "Name":
        endpoint = `getPatientByName/${searchValue}`;
        break;
      case "CNIC":
        endpoint = `getPatientByCNIC/${searchValue}`;
        break;
      case "MRN":
        endpoint = `getPatientByMRN/${searchValue}`;
        break;
      case "Phone Number":
        endpoint = `getPatientByPhonNo/${searchValue}`;
        break;
      default:
        console.error("Invalid search type");
        setLoading(false);
        return;
    }

    fetch(
      `${process.env.REACT_APP_BASE_URL}/patient-registration/${endpoint}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    )
      .then((response) => {
        if (response.ok) return response.json();
        throw new Error("Network response was not ok");
      })
      .then((data) => {
        const patients = data?.data;
        setSelectedId(patients?._id || "");

        // Handle single patient case
        if (patients && !Array.isArray(patients)) {
          toast.success("Record Found");
          setStoreData(patients);
          formik.setValues(patients);
          const patientId = patients;
          
          // Check if doctor is already saved in localStorage
          const lastDoctorId = localStorage.getItem('lastSelectedDoctorSlot');
          if (lastDoctorId) {
            navigateWithDoctor(patientId, lastDoctorId);
          } else {
            navigate("/doc_card", { state: { patientId } });
          }
        } else if (Array.isArray(patients)) {
          // Handle multiple patients case
          if (patients.length === 1) {
            // Single patient in array
            toast.success("Record Found");
            setStoreData(patients[0]);
            formik.setValues(patients[0]);
            setSelectedId(patients[0]?._id || "");
            const patientId = patients[0];
            
            // Check if doctor is already saved in localStorage
            const lastDoctorId = localStorage.getItem('lastSelectedDoctorSlot');
            if (lastDoctorId) {
              navigateWithDoctor(patientId, lastDoctorId);
            } else {
              navigate("/doc_card", { state: { patientId } });
            }
          } else if (patients.length > 1) {
            // Multiple patients, open modal
            const patientGuardianNames: ModalData[] = patients.map(
              (patient: any) => ({
                patientName: patient.patientName,
                guardiansName: patient.guardiansName,
                mrn: patient.mrn,
              })
            );
            setModalData(patientGuardianNames);
            handleOpenModal();
          } else {
            // No patients found
            toast.error("Record Not Found");
          }
        } else {
          // No patients found
          toast.error("Record Not Found");
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        toast.error("Record Not Found");
      })
      .finally(() => setLoading(false));
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") handleSearch();
  };

  const clearFields = () => {
    setSelectedId("");
    setAge("");
    formik.resetForm();
  };

  const handleCNICPhoneSearch = (mrn: number) => {
    setSelectedMRN(mrn);
    handleCloseModal();
  };

  useEffect(() => {
    if (selectedMRN) {
      axios
        .get(
          `${process.env.REACT_APP_BASE_URL}/patient-registration/getPatientByMRN/${selectedMRN}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        )
        .then((response) => {
          const patientData = response.data?.data;
          setSelectedId(patientData?._id || "");
          setStoreData(patientData);
          formik.setValues(patientData);
          toast.success("Record Found");
          const patientId = patientData;
          
          // Check if doctor is already saved in localStorage
          const lastDoctorId = localStorage.getItem('lastSelectedDoctorSlot');
          if (selectedmrn) {
            navigate("/patient-registration-slot");
          } else {
            if (lastDoctorId) {
              navigateWithDoctor(patientId, lastDoctorId);
            } else {
              navigate("/doc_card", { state: { patientId } });
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching patient data:", error);
          toast.error("Record Not Found");
        });
    }
  }, [selectedMRN]);

  const DateOfBirthToAge = (dob: string): string => {
    const dobDate = new Date(dob);
    const currentDate = new Date();
    let years = currentDate.getFullYear() - dobDate.getFullYear();
    let months = currentDate.getMonth() - dobDate.getMonth();
    let days = currentDate.getDate() - dobDate.getDate();
    if (months < 0 || (months === 0 && days < 0)) {
      years--;
      months += 12;
    }
    if (days < 0) {
      const prevMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        dobDate.getDate()
      );
      const diff =
        (currentDate.getTime() - prevMonth.getTime()) / (1000 * 60 * 60 * 24);
      days = Math.floor(diff);
    }
    return `${years.toString().padStart(2, "0")}-${months
      .toString()
      .padStart(2, "0")}-${days.toString().padStart(2, "0")}`;
  };

  const handleChangeAge = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parts = e.target.value.split("-");
    const year = parseInt(parts[0] || "0", 10) > 99 ? "00" : parts[0];
    const month = parseInt(parts[1] || "0", 10) > 12 ? "00" : parts[1];
    const day = parseInt(parts[2] || "0", 10) > 99 ? "00" : parts[2];
    const correctedValue = `${year}-${month}-${day}`;
    setAge(correctedValue);
    const age = calculateDateOfBirth(correctedValue);
    formik.setFieldValue("dob", age);
  };

  const calculateDateOfBirth = (age: string): string => {
    const [years, months, days] = age.split("-").map(Number);
    const currentDate = new Date();
    const dateOfBirth = new Date(
      currentDate.getFullYear() - years,
      currentDate.getMonth() - months,
      currentDate.getDate() - days
    );
    return `${dateOfBirth.getFullYear()}-${(dateOfBirth.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${dateOfBirth.getDate().toString().padStart(2, "0")}`;
  };

  return (
    <Box sx={{ background: "#fff", padding: "10px 24px" }}>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <>
          <SearchModal
            open={openModal}
            handleClose={handleCloseModal}
            data={modalData}
            cnicphone={formik?.values?.cnic || formik?.values?.phonNumber || ""}
            sendMRN={handleCNICPhoneSearch}
          />
          <Grid
            container
            justifyContent={"space-between"}
            alignItems={"center"}
            sx={{ my: 2 }}
          >
            <Grid item md={12} sm={12} xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap={"wrap"}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { md: "20px", xs: "18px" },
                    textTransform: "capitalize",
                    fontWeight: 600,
                    p: 0,
                  }}
                >
                  Patient Registration
                </Typography>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <Box display="flex" gap={1} alignItems="center">
                    <Typography sx={{ fontSize: { md: "14px", xs: "12px" } }}>
                      Search By:
                    </Typography>
                    <Select
                      value={searchType}
                      onChange={handleSearchTypeChange}
                      sx={{
                        borderRadius: "12px",
                        border: "0px solid #D8D8D8",
                        background: "#F5F5FB",
                        fontSize: "12px",
                        height: "38px",
                        "& fieldset": { border: "none" },
                      }}
                      input={<OutlinedInput style={{ border: "none" }} />}
                    >
                      <MenuItem
                        value="MRN"
                        sx={{ fontSize: { md: "14px", xs: "12px" } }}
                      >
                        MRN
                      </MenuItem>
                      <MenuItem
                        value="Name"
                        sx={{ fontSize: { md: "14px", xs: "12px" } }}
                      >
                        Name
                      </MenuItem>
                      <MenuItem
                        value="CNIC"
                        sx={{ fontSize: { md: "14px", xs: "12px" } }}
                      >
                        CNIC
                      </MenuItem>
                      <MenuItem
                        value="Phone Number"
                        sx={{ fontSize: { md: "14px", xs: "12px" } }}
                      >
                        Mobile #
                      </MenuItem>
                    </Select>
                  </Box>
                  <Box display="flex" gap={1} alignItems="center">
                    <Typography sx={{ fontSize: { md: "14px", xs: "12px" } }}>
                      Search Value:
                    </Typography>
                    <TextField
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder={`Enter ${searchType}`}
                      size="small"
                      className="search-field"
                      type={
                        searchType === "Name" || searchType === "Phone Number"
                          ? "text"
                          : "number"
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {formik?.values?.mrn &&
                            formik?.values?.patientName ? (
                              <IconButton onClick={clearFields}>
                                <Clear sx={{ fontSize: { md: "16px" } }} />
                              </IconButton>
                            ) : (
                              <IconButton
                                disabled={searching || loading}
                                onClick={handleSearch}
                              >
                                <SearchIcon sx={{ fontSize: { md: "16px" } }} />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                      onKeyPress={handleKeyPress}
                    />
                  </Box>
                </div>
              </Box>
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2 }} />
          <Box mt={2}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#3F3F3F",
                      fontWeight: "600",
                    }}
                  >
                    Patient Name:
                  </Typography>
                  <TextField
                    className="search-field"
                    placeholder="Patient Name"
                    name="patientName"
                    fullWidth
                    size="small"
                    onChange={handleInputChange}
                    value={formik?.values?.patientName}
                    error={
                      formik?.touched?.patientName &&
                      Boolean(formik?.errors?.patientName)
                    }
                    helperText={
                      formik?.touched?.patientName &&
                      formik?.errors?.patientName
                    }
                  />
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#3F3F3F",
                      fontWeight: "600",
                    }}
                  >
                    Guardian Name:
                  </Typography>
                  <TextField
                    className="search-field"
                    placeholder="Guardian Name"
                    name="guardiansName"
                    fullWidth
                    size="small"
                    onChange={handleInputChange}
                    value={formik?.values?.guardiansName}
                    error={
                      formik?.touched?.guardiansName &&
                      Boolean(formik?.errors?.guardiansName)
                    }
                    helperText={
                      formik?.touched?.guardiansName &&
                      formik?.errors?.guardiansName
                    }
                  />
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#3F3F3F",
                      fontWeight: "600",
                    }}
                  >
                    Gender:
                  </Typography>
                  <RadioGroup
                    name="gender"
                    row
                    onChange={handleInputChange}
                    value={formik?.values?.gender}
                  >
                    <FormControlLabel
                      value="Male"
                      control={<Radio size="small" />}
                      label="Male"
                    />
                    <FormControlLabel
                      value="Female"
                      control={<Radio size="small" />}
                      label="Female"
                    />
                    <FormControlLabel
                      value="Other"
                      control={<Radio size="small" />}
                      label="Other"
                    />
                  </RadioGroup>
                  {formik.touched.gender && formik.errors.gender && (
                    <Typography variant="caption" color="error">
                      {formik?.errors?.gender}
                    </Typography>
                  )}
                </Grid>
                <Grid item md={1.5} sm={3} xs={6}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#3F3F3F",
                      fontWeight: "600",
                    }}
                  >
                    Date of Birth:
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="DD.MM.YYYY"
                      value={
                        formik?.values?.dob ? dayjs(formik?.values?.dob) : null
                      }
                      onChange={(date) => {
                        if (date && dayjs(date).isValid()) {
                          const formattedDate =
                            dayjs(date).format("YYYY-MM-DD");
                          if (validateDate(dayjs(date).format("DD.MM.YYYY"))) {
                            formik.setFieldValue("dob", formattedDate);
                            let convertDOB = DateOfBirthToAge(formattedDate);
                            setAge(convertDOB);
                            setInputError("");
                          } else {
                            formik.setFieldValue("dob", "");
                            setAge("");
                            setInputError("Please enter a valid date");
                          }
                        } else {
                          formik.setFieldValue("dob", "");
                          setAge("");
                        }
                      }}
                      maxDate={dayjs()}
                      minDate={dayjs("1900-01-01")}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error:
                            (formik.touched.dob &&
                              Boolean(formik.errors.dob)) ||
                            Boolean(inputError),
                          helperText:
                            (formik.touched.dob && formik.errors.dob) ||
                            inputError,
                          onKeyDown: (e) => {
                            const allowedKeys = [
                              "Backspace",
                              "ArrowLeft",
                              "ArrowRight",
                              "Tab",
                              "Period",
                              ".",
                            ];
                            const isNumber = /[0-9]/.test(e.key);
                            if (!isNumber && !allowedKeys.includes(e.key)) {
                              e.preventDefault();
                            }
                          },
                          onChange: (
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            try {
                              const value = e.target.value;
                              if (value.length === 2 && !value.includes(".")) {
                                e.target.value = value + ".";
                              } else if (
                                value.length === 5 &&
                                value.charAt(2) === "." &&
                                !value.includes(".", 3)
                              ) {
                                e.target.value = value + ".";
                              }
                              const parts = value.split(".");
                              const day = parts[0] || "";
                              const month = parts.length > 1 ? parts[1] : "";
                              const year = parts.length > 2 ? parts[2] : "";
                              if (
                                day &&
                                month &&
                                year &&
                                day.length === 2 &&
                                month.length === 2 &&
                                year.length === 4
                              ) {
                                const dateStr = `${day}.${month}.${year}`;
                                if (validateDate(dateStr)) {
                                  const formattedDate = `${year}-${month}-${day}`;
                                  formik.setFieldValue("dob", formattedDate);
                                  let convertDOB =
                                    DateOfBirthToAge(formattedDate);
                                  setAge(convertDOB);
                                  setInputError("");
                                }
                              }
                            } catch (error) {
                              console.error(
                                "Error in date input handler:",
                                error
                              );
                            }
                          },
                          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                            try {
                              const value = e.target.value;
                              if (!value) {
                                setInputError("");
                                formik.handleBlur(e);
                                return;
                              }
                              if (value && !validateDate(value)) {
                                setInputError("Please enter a valid date");
                                formik.setFieldValue("dob", "");
                                setAge("");
                              } else if (value) {
                                const parts = value.split(".");
                                if (parts.length === 3) {
                                  const day = parts[0].padStart(2, "0");
                                  const month = parts[1].padStart(2, "0");
                                  const year = parts[2].padStart(4, "0");
                                  const formattedDate = `${year}-${month}-${day}`;
                                  formik.setFieldValue("dob", formattedDate);
                                  let convertDOB =
                                    DateOfBirthToAge(formattedDate);
                                  setAge(convertDOB);
                                  setInputError("");
                                }
                              }
                              formik.handleBlur(e);
                            } catch (error) {
                              console.error(
                                "Error in date blur handler:",
                                error
                              );
                              formik.handleBlur(e);
                            }
                          },
                        },
                      }}
                      openTo="year"
                      views={["year", "month", "day"]}
                      sx={{
                        "& fieldset": { border: "none" },
                        "& .MuiInputBase-root": {
                          height: "38px",
                          fontSize: "12px",
                          overflow: "hidden",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          border: "none",
                          backgroundColor: "#F5F5FB",
                        },
                        "& .MuiCalendarPicker-root": { overflow: "hidden" },
                        "& .css-4jnixx-MuiStack-root": { paddingTop: "0px" },
                        width: "100%",
                      }}
                    />
                  </LocalizationProvider>
                  {formik.touched.dob && formik.errors.dob && (
                    <Typography variant="caption" color="error">
                      {formik.errors.dob}
                    </Typography>
                  )}
                </Grid>
                <Grid item md={1.5} sm={3} xs={6}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#3F3F3F",
                      fontWeight: "600",
                    }}
                  >
                    YY-MM-DD:
                  </Typography>
                  <InputMask
                    mask="99-99-99"
                    value={
                      age ? age : DateOfBirthToAge(dayjs().format("YYYY-MM-DD"))
                    }
                    onChange={handleChangeAge}
                    maskChar=""
                  >
                    {() => (
                      <TextField
                        className="search-field"
                        fullWidth
                        placeholder="YY-MM-DD"
                        size="small"
                      />
                    )}
                  </InputMask>
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#3F3F3F",
                      fontWeight: "600",
                    }}
                  >
                    Phone Number:
                  </Typography>
                  <InputMask
                    mask="9999-9999999"
                    value={formik?.values?.phonNumber}
                    onChange={handleInputChange}
                    maskChar=" "
                  >
                    {() => (
                      <TextField
                        placeholder="Phone Number"
                        name="phonNumber"
                        fullWidth
                        className="search-field"
                        size="small"
                        error={
                          formik?.touched?.phonNumber &&
                          Boolean(formik?.errors?.phonNumber)
                        }
                        helperText={
                          formik?.touched?.phonNumber &&
                          formik?.errors?.phonNumber
                        }
                      />
                    )}
                  </InputMask>
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#3F3F3F",
                      fontWeight: "600",
                    }}
                  >
                    CNIC:
                  </Typography>
                  <InputMask
                    mask="99999-9999999-9"
                    onChange={handleInputChange}
                    value={formik?.values?.cnic}
                    maskChar=" "
                  >
                    {() => (
                      <TextField
                        className="search-field"
                        placeholder="CNIC"
                        name="cnic"
                        fullWidth
                        size="small"
                        error={
                          formik.touched.cnic && Boolean(formik.errors.cnic)
                        }
                        helperText={formik.touched.cnic && formik.errors.cnic}
                      />
                    )}
                  </InputMask>
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#3F3F3F",
                      fontWeight: "600",
                    }}
                  >
                    Health Id (optional):
                  </Typography>
                  <TextField
                    className="search-field"
                    placeholder="Health Id"
                    name="healthId"
                    fullWidth
                    size="small"
                    onChange={handleInputChange}
                    value={formik?.values?.healthId}
                    error={
                      formik?.touched?.healthId &&
                      Boolean(formik.errors.healthId)
                    }
                    helperText={
                      formik.touched.healthId && formik.errors.healthId
                    }
                  />
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#3F3F3F",
                      fontWeight: "600",
                    }}
                  >
                    City:
                  </Typography>
                  <TextField
                    className="search-field"
                    placeholder="City"
                    name="city"
                    fullWidth
                    size="small"
                    onChange={handleInputChange}
                    value={formik?.values?.city}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                  />
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#3F3F3F",
                      fontWeight: "600",
                    }}
                  >
                    Reference (optional):
                  </Typography>
                  <TextField
                    className="search-field"
                    placeholder="Reference"
                    name="reference"
                    fullWidth
                    size="small"
                    onChange={handleInputChange}
                    value={formik?.values?.reference}
                  />
                </Grid>
              </Grid>
              <Box
                display="flex"
                justifyContent="end"
                alignItems="center"
                mt={2}
                gap={2}
              >
                <Button onClick={() => navigate("/appointment-dashboard")}>
                  Back
                </Button>
                {selectedId && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ px: 4, py: 1.5 }}
                    onClick={() => setIsUpdateAndNext(false)} // Set flag for Update
                    type="submit"
                  >
                    Update
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  sx={{ px: 4, py: 1.5, ml: 1 }}
                  onClick={() => setIsUpdateAndNext(true)} // Set flag for Update & Next
                >
                  {selectedId ? "Update & Next" : "Save"}
                </Button>
              </Box>
            </form>
          </Box>
        </>
      )}
    </Box>
  );
};

export default RegForm;
